import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Loader2, Send } from "lucide-react";

import { eur } from "@/lib/format";

export const waDigits = (raw) => {
  let d = (raw || "").replace(/[^\d]/g, "");
  if (d.length === 10 && !(raw || "").startsWith("+") && !d.startsWith("39")) d = "39" + d;
  return d;
};

const testoSollecito = (it) => {
  const nome = (it.cliente || "").split(" ")[0] || "";
  return `Buongiorno ${nome},\n\nle scrivo in merito alla pratica "${it.tipo_pratica}" seguita dal nostro studio.\nRisulta ancora da saldare un importo di ${eur(it.residuo)} su un totale di ${eur(it.importo_totale)}.\n\nLe chiedo gentilmente di procedere al pagamento o di indicarmi quando potrà farlo, così da procedere senza ritardi con la pratica.\n\nResto a disposizione per qualsiasi chiarimento.\nCordiali saluti,\nRoberta Barbierato — RB Studio`;
};

// Dialog di sollecito pagamento: testo modificabile, WhatsApp o bozza Gmail.
// Ogni sollecito viene registrato nel diario della pratica.
export default function SollecitoDialog({ it, onClose }) {
  const qc = useQueryClient();
  const [oggetto, setOggetto] = useState(`Pratica ${it.tipo_pratica} — saldo di ${eur(it.residuo)}`);
  const [testo, setTesto] = useState(testoSollecito(it));
  const [busy, setBusy] = useState(false);
  const digits = waDigits(it.telefono);

  const registra = async (canale) => {
    try {
      await api.post(`/pratiche/${it.pratica_id}/sollecito-log`, { canale });
      qc.invalidateQueries({ queryKey: ["solleciti"] });
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      qc.invalidateQueries({ queryKey: ["pratica", it.pratica_id] });
    } catch (e) {
      console.warn("Registrazione sollecito nel diario non riuscita:", e);
    }
  };

  const apriWhatsapp = async () => {
    if (!digits) return toast.error("Nessun numero disponibile per questo cliente");
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(testo)}`, "_blank");
    await registra("whatsapp");
    toast.success("Sollecito annotato nel diario della pratica");
    onClose();
  };

  const creaBozza = async () => {
    if (!it.email) return toast.error("Questo cliente non ha un indirizzo email in anagrafica");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("to", it.email);
      fd.append("oggetto", oggetto);
      fd.append("testo", testo);
      const { data } = await api.post(`/pratiche/${it.pratica_id}/email-draft`, fd);
      await registra("email");
      toast.success("Bozza creata in Gmail (non inviata) e sollecito annotato nel diario", {
        action: data.draft_link ? { label: "Apri", onClick: () => window.open(data.draft_link, "_blank") } : undefined,
      });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bozza non creata");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#1a1a1a]/50 flex items-center justify-center p-4" data-testid="sollecito-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-3 border-b border-[#1A2B4A]/10">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-[#4a5568]">Sollecito pagamento</p>
            <h3 className="font-serif text-xl text-[#1A2B4A]">{it.cliente} · {eur(it.residuo)}</h3>
            {it.ultimo_sollecito && (
              <p className="text-[11px] text-[#8a6d1f] mt-0.5" data-testid="sollecito-ultimo">
                Ultimo sollecito: {new Date(it.ultimo_sollecito).toLocaleDateString("it-IT")}
                {it.ultimo_sollecito_canale ? ` (${it.ultimo_sollecito_canale})` : ""}
              </p>
            )}
          </div>
          <button data-testid="sollecito-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1">Oggetto (solo email)</label>
            <input data-testid="sollecito-oggetto" value={oggetto} onChange={(e) => setOggetto(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1">Messaggio (modificabile)</label>
            <textarea data-testid="sollecito-testo" value={testo} onChange={(e) => setTesto(e.target.value)} rows={11}
              className="w-full px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <p className="text-[11px] text-[#4a5568]">
            WhatsApp: {digits ? `+${digits}${it.telefono_fonte === "rif" ? " (numero del Rif.)" : ""}` : "nessun numero"} ·
            Email: {it.email || "non presente"}. La mail viene creata come <b>bozza</b> in Gmail, mai inviata automaticamente.
            Ogni sollecito viene annotato nel diario della pratica.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-3 px-6 py-3 border-t border-[#1A2B4A]/10">
          <button data-testid="sollecito-whatsapp" onClick={apriWhatsapp} disabled={!digits}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[#276749] text-[#276749] rounded-sm hover:bg-[#e7f2ec] disabled:opacity-40">
            <MessageCircle className="w-4 h-4" /> Apri WhatsApp
          </button>
          <button data-testid="sollecito-email" onClick={creaBozza} disabled={busy || !it.email}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Crea bozza Gmail
          </button>
        </div>
      </div>
    </div>
  );
}
